import { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Col, Input, Modal, Row, Switch, Tooltip } from 'antd';

import { EChargeType } from 'interfaces/IEditProduct';
import type IProduct from 'interfaces/IProduct';
import { EPartnerCode } from 'interfaces/IProduct';
import { run } from 'validations/ruleRunner';

import { FormContent } from '../FormContent';
import Validations from './validations';

interface IProps {
  loading: boolean;
  fade?: any;
  product?: IProduct;
  modifieds?: any[];
  editing?: any;
  handleChange?: any;
  copyFromOriginal?: any;
  onChangeRequiredFields?: any;
}

function SalesRecovery(props: IProps) {
  const { loading, handleChange, product, onChangeRequiredFields } = props;
  const [validationErrors, setValidationErrors] = useState(() => run(product, Validations));
  const [showAlertUncheck, setShowAlertUncheck] = useState(false);

  const prevProductRef = useRef(product);
  const hasRunOnMountRef = useRef(false);

  const errorFor = useCallback(
    (field: any) => {
      return validationErrors[field] || '';
    },
    [validationErrors]
  );

  const isValid = useCallback(
    (field: any) => {
      return validationErrors[field] ? true : false;
    },
    [validationErrors]
  );

  const handleToggleAlertVisibility = useCallback(() => {
    setShowAlertUncheck(prev => !prev);
  }, []);

  const handleUncheckConfirmed = useCallback(
    (currentProduct: IProduct) => {
      currentProduct.recovery_email = '';

      if (handleChange) {
        handleChange('recovery_email', '');
        handleChange('sales_recovery', false);
      }

      handleToggleAlertVisibility();
    },
    [handleChange, handleToggleAlertVisibility]
  );

  const onChangeStates = useCallback(() => {
    if (!product) {
      return;
    }

    let newValidations;

    if (product.charge_type === EChargeType.SINGLE) {
      newValidations = {
        ...run(product, Validations)
      };
    } else {
      if (product.charge_type === EChargeType.SUBSCRIPTION) {
        newValidations = {
          ...run(product, Validations)
        };
      } else {
        newValidations = { ...run(product, Validations) };
      }
    }

    setValidationErrors(newValidations);

    if (onChangeRequiredFields) {
      onChangeRequiredFields('salesRequiredFields', newValidations ? Object.keys(newValidations).length : 0);
    }
  }, [product, onChangeRequiredFields]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const productChanged = prevProductRef.current !== product;

    if (!hasRunOnMountRef.current) {
      hasRunOnMountRef.current = true;
      onChangeStates();
    } else {
      if (productChanged) {
        onChangeStates();
      }
    }

    prevProductRef.current = product;
  }, [loading, product, onChangeStates]);

  const isAlumy = product?.cnt_partner_cod === EPartnerCode.ALUMY;
  const isOpenValue = product?.charge_type === EChargeType.OTHER;

  if (!product) {
    return null;
  }

  return (
    <>
      <Row gutter={[16, 16]} align='middle'>
        <Col span={24}>
          <FormContent title='Recuperação de vendas' loading={loading}>
            <Row gutter={[16, 16]} align='middle'>
              <Col span={24}>
                <Tooltip
                  title={isOpenValue ? '*Não é possível ativar a recuperação com Valor Aberto' : ''}
                  placement='left'
                >
                  <div>
                    <div className='flex items-center gap-2'>
                      <Switch
                        checked={product.sales_recovery}
                        disabled={isOpenValue || isAlumy}
                        onChange={checked => {
                          if (checked) {
                            if (handleChange) {
                              handleChange('sales_recovery', checked);
                            }

                            return;
                          }

                          setShowAlertUncheck(true);
                        }}
                      />
                      <span>Recuperar vendas perdidas</span>
                    </div>
                  </div>
                </Tooltip>
                <p className='mt-1! mb-0! text-xs text-gray-500'>
                  A Eduzz tentará recuperar vendas não concretizadas.{' '}
                  <a
                    target='_blank'
                    rel='noopener noreferrer'
                    href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807625289-Como-funciona-a-recupera%C3%A7%C3%A3o-de-vendas-perdidas-RVP-'
                  >
                    Saiba Mais
                  </a>
                </p>
              </Col>
              {product.sales_recovery && (
                <Col span={24}>
                  <Tooltip title='O nome definido neste campo aparecerá como remetente nos e-mails enviados de Recuperação de Vendas.'>
                    <div className='relative'>
                      <label className='mb-1 block text-sm'>
                        Nome do remetente de e-mail para recuperação de vendas
                      </label>
                      <Input
                        id='recovery_email'
                        className='w-full'
                        status={isValid('recovery_email') ? 'error' : undefined}
                        value={product.recovery_email}
                        onChange={e => {
                          if (handleChange) {
                            handleChange('recovery_email', e.target.value);
                          }
                        }}
                        disabled={!product.sales_recovery}
                      />
                      <p className='mt-1! mb-0! text-xs text-gray-500'>
                        {isValid('recovery_email') ? errorFor('recovery_email') : 'Ex: Fulano da Silva - Eduzz'}
                      </p>
                    </div>
                  </Tooltip>
                </Col>
              )}
            </Row>
          </FormContent>
        </Col>
      </Row>

      <Modal
        open={showAlertUncheck}
        onCancel={handleToggleAlertVisibility}
        title='Tem certeza que deseja desativar esta funcionalidade?'
        footer={[
          <Button key='deactivate' onClick={() => handleUncheckConfirmed(product)}>
            Desativar mesmo assim
          </Button>,
          <Button key='keep' type='primary' onClick={handleToggleAlertVisibility}>
            Manter ativado
          </Button>
        ]}
      >
        <p>
          Mantenha a RVP ativa para recuperar até 10% das suas vendas e aumentar suas conversões com clientes que
          abandonam o carrinho de forma automática.
        </p>
      </Modal>
    </>
  );
}

export default SalesRecovery;
